import React from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import { useCookieConsent } from "@/context/CookieContext";

const columns = [
  {
    title: "Platforms",
    links: [
      { label: "Bambereaux Platform", to: "/bambereaux", testId: "footer-link-bambereaux" },
      { label: "Agro-Industrial Platform", to: "/agro-industrial-platform", testId: "footer-link-agro" },
      { label: "All Projects", to: "/projects", testId: "footer-link-projects" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About", to: "/about", testId: "footer-link-about" },
      { label: "Governance", to: "/governance", testId: "footer-link-governance" },
      { label: "Investor Relations", to: "/investor-relations", testId: "footer-link-investor" },
      { label: "Contact", to: "/contact", testId: "footer-link-contact" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy Policy", to: "/privacy-policy", testId: "footer-link-privacy" },
      { label: "Cookie Policy", to: "/cookie-policy", testId: "footer-link-cookie" },
      { label: "Terms of Use", to: "/terms-of-use", testId: "footer-link-terms" },
    ],
  },
];

export default function Footer() {
  const { openPreferences } = useCookieConsent();
  const year = new Date().getFullYear();

  return (
    <footer data-testid="site-footer" className="bg-[#0A1F44] text-white">
      <div className="evg-container pt-28 pb-12">
        {/* Headline */}
        <div className="max-w-4xl pb-20" data-reveal>
          <div className="evg-overline-gold mb-6">Engage</div>
          <p className="evg-display-md text-white">
            A measured conversation with our investor relations team.
          </p>
          <Link
            to="/investor-relations"
            data-testid="footer-engage-cta"
            className="mt-10 inline-block text-[11px] uppercase tracking-[0.24em] font-medium text-white border-b border-[#D4AF37] pb-2 hover:text-[#D4AF37] transition-colors"
          >
            Discuss Investment Opportunities
          </Link>
        </div>

        {/* Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-12 pt-16 border-t border-white/12">
          <div className="lg:col-span-5">
            <Logo variant="light" testId="footer-logo" />
            <p className="mt-8 max-w-md text-[13px] leading-[1.8] text-white/65 font-light">
              Evergreen Global Holdings Ltd is a UK-based development sponsor, investment platform
              and governance-led holding company creating long-term value through strategically
              important projects across the Caribbean.
            </p>
            <div className="mt-10 flex flex-col gap-3">
              <a
                href="mailto:investors@evergreenglobalholdings.com"
                data-testid="footer-email"
                className="text-[13px] text-white/65 hover:text-white transition-colors"
              >
                investors@evergreenglobalholdings.com
              </a>
              <a
                href="https://www.linkedin.com"
                target="_blank"
                rel="noreferrer noopener"
                data-testid="footer-linkedin"
                className="text-[13px] text-white/65 hover:text-white transition-colors"
              >
                LinkedIn — Evergreen Global Holdings Ltd
              </a>
              <span className="text-[13px] text-white/65">United Kingdom</span>
            </div>
          </div>

          {columns.map((col) => (
            <div key={col.title} className="lg:col-span-2">
              <div className="evg-overline-gold">{col.title}</div>
              <ul className="mt-6 space-y-3">
                {col.links.map((l) => (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      data-testid={l.testId}
                      className="text-[13px] text-white/70 hover:text-white transition-colors font-light"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="lg:col-span-1">
            <div className="evg-overline-gold">Office</div>
            <div className="mt-6 text-[13px] text-white/70 font-light">United Kingdom</div>
          </div>
        </div>

        {/* Bottom strip */}
        <div className="mt-20 pt-8 border-t border-white/12 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="text-[11px] text-white/45 uppercase tracking-[0.18em] font-light">
            © {year} Evergreen Global Holdings Ltd · Registered in the United Kingdom
          </p>
          <div className="flex flex-wrap items-center gap-x-7 gap-y-2">
            <Link to="/privacy-policy" className="text-[11px] uppercase tracking-[0.18em] text-white/45 hover:text-white" data-testid="footer-bottom-privacy">
              Privacy Policy
            </Link>
            <Link to="/cookie-policy" className="text-[11px] uppercase tracking-[0.18em] text-white/45 hover:text-white" data-testid="footer-bottom-cookie">
              Cookie Policy
            </Link>
            <Link to="/terms-of-use" className="text-[11px] uppercase tracking-[0.18em] text-white/45 hover:text-white" data-testid="footer-bottom-terms">
              Terms of Use
            </Link>
            <button
              type="button"
              onClick={openPreferences}
              data-testid="footer-cookie-settings"
              className="text-[11px] uppercase tracking-[0.18em] text-white/45 hover:text-white"
            >
              Cookie Settings
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
